// THE REVERSE VIEW OF THE CALENDAR — every meeting with one client, in one place.
//
// The calendar is organised by DATE and answers "what is on Tuesday". This is organised by
// CLIENT and answers the two questions that come up when you open their card: when do we
// next see them, and what was agreed last time. Neither view can answer the other's
// question, which is why this is a tab and not a link to a filtered calendar.
//
// DISPLAY ONLY. Nothing here books, edits, summarises or cancels — all of that lives in the
// calendar. The soonest upcoming meeting is the first row; it is not stated a second time. It is the same division the client-documents screen makes: the screen that can
// change a thing is the screen that owns it.
//
// IT SORTS AND GROUPS NOTHING. The server hands back { upcoming, past, cancelled }, already
// ordered, because where a meeting sits in time is decided by calculations/dueState — the
// one date rule a task, an invoice, a lead follow-up and now a meeting all share
// (`npm run check:due`). A browser that re-derived "has this happened" would be a browser
// holding a second opinion about it.
//
// NO IMPORTS FROM pages/Calendar. Every fact a row needs — `state`, `cancelled`, `held`,
// `duration_minutes`, `actual_minutes`, `job_title`, `owner_name` — is already stamped on
// the shaped row by the server, so this tab needs none of the calendar's components and
// neither page has to be disturbed to share them.
import useAsync from '../../hooks/useAsync';
import he from '../../i18n/he';
import { meetingsApi } from '../../api/calendar';
import AsyncBoundary from '../../components/Loader';
import EmptyState from '../../components/EmptyState';
import DateText from '../../components/DateText';

// The length, in the calendar's own words ('שעה', '1:30 שעות', '30 דקות').
const length = (minutes) => (minutes ? he.calendar.minutes(minutes) : null);

// One meeting. A header line of facts, and the summary beneath it in its own right — a
// summary is prose, and a table cell would crush it into an ellipsis. That is the whole
// reason this is a list of rows rather than a DataTable.
function MeetingRow({ meeting }) {
  const cancelled = meeting.cancelled;
  return (
    <li className={`client-meeting${cancelled ? ' cancelled' : ''}`}>
      <div className="client-meeting-head">
        <span className="client-meeting-when">
          <DateText value={meeting.meeting_date} />
          <span className="muted"> · {meeting.start_time}–{meeting.end_time}</span>
        </span>

        <span className="muted small-note">{length(meeting.duration_minutes)}</span>

        {/* The job the meeting was about, when it was about one. Most are not, so this is
            absent rather than showing a dash. */}
        {meeting.job_title && <span className="client-meeting-job">{meeting.job_title}</span>}

        {/* Booked with someone outside the system and linked here later (077): who was met. */}
        {meeting.external_name && (
          <span className="muted small-note">{he.calendar.metWith(meeting.external_name)}</span>
        )}

        <span className="muted small-note">{meeting.owner_name}</span>

        {/* One badge, and it reports the row's own `state` — never a second opinion formed
            here. 'התקיימה' is the exception: it is `held`, a separate fact from the date,
            and it is worth saying because it means a summary was actually written. */}
        {cancelled
          ? <span className="badge gray">{he.calendar.cancelled}</span>
          : (meeting.held
            ? <span className="badge green">{he.calendar.held}</span>
            : <span className="badge blue">{he.calendar.scheduled}</span>)}
      </div>

      {cancelled && meeting.cancel_reason && (
        <p className="muted small-note client-meeting-reason">{meeting.cancel_reason}</p>
      )}

      {/* THE ACTUAL LENGTH SITS BESIDE THE SCHEDULED ONE, never in place of it. The hour
          that was booked and the hour it took are two different facts (migration 055), and
          overwriting the first with the second would destroy what was agreed. */}
      {meeting.actual_minutes != null && (
        <p className="muted small-note">{he.calendar.clientActual(length(meeting.actual_minutes))}</p>
      )}

      {meeting.summary && <p className="client-meeting-summary">{meeting.summary}</p>}
    </li>
  );
}

const Section = ({ title, meetings, tone }) => (
  meetings.length === 0 ? null : (
    <section className={`client-meeting-section${tone ? ` ${tone}` : ''}`}>
      <h3 className="section-title">{title} <span className="muted">({meetings.length})</span></h3>
      <ul className="client-meeting-list">
        {meetings.map((m) => <MeetingRow key={m.id} meeting={m} />)}
      </ul>
    </section>
  )
);

export default function MeetingsTab({ clientId }) {
  const { data, loading, error } = useAsync(() => meetingsApi.byClient(clientId), [clientId]);
  const { upcoming = [], past = [], cancelled = [] } = data || {};
  const total = upcoming.length + past.length + cancelled.length;
  return (
    <AsyncBoundary loading={loading} error={error}>
      {data && (
        total === 0 ? <EmptyState text={he.calendar.clientEmpty} /> : (
          <>
            <Section title={he.calendar.clientUpcoming} meetings={upcoming} />
            <Section title={he.calendar.clientPast} meetings={past} />
            {/* Last, and set apart: a cancelled meeting is neither coming up nor a real
                past meeting, so it belongs in neither list — but it stays visible, because
                "they cancelled twice" is a thing the history should be able to say. */}
            <Section title={he.calendar.clientCancelled} meetings={cancelled} tone="muted-section" />
          </>
        )
      )}
    </AsyncBoundary>
  );
}
